<template>
    <v-card class="elevation-1 rounded-4"> 
        <v-toolbar flat height="45px">
            <span class="font-weight-bold subheading">Log in faster next time!</span>
            <v-spacer></v-spacer>
        </v-toolbar>
        <v-progress-linear v-if="loading" indeterminate height="2" class="mt-0"></v-progress-linear>

        <v-alert v-if="error" :value="true" color="error" icon="error" outline class="mt-8 mr-5 ml-5" dismissible>
            <div class="font-weight-medium mb-1">{{ errorTitle }}</div>
            <div class="mb-2 grey--text text--darken-3">{{ errorMessage }}</div>
            <span class="clickable underlined primary--text" @click="openDeviceLoginUserGuide">Learn more about Device Login<v-icon class="ml-1" small>open_in_new</v-icon></span>
        </v-alert>
        
        <v-card-text class="text-xs-center">
            <v-layout row wrap>
                <v-flex xs12 sm3 v-if="!isMobile">
                    <v-icon color="primary" size="75" :class="accessedFromMyDashboard?'mt-15':''">fingerprint</v-icon>
                </v-flex> 
                <v-flex xs12 sm9>
                    <p :class="!accessedFromMyDashboard?'msg-text mt-15':'msg-text'">
                        Use your fingerprint, face, or PIN to log in — no password or OTP needed.
                        <span class="clickable underlined primary--text ml-1" @click="openDeviceLoginUserGuide">Learn more<v-icon class="ml-1" small>open_in_new</v-icon></span>
                    </p>
                    <p v-if="accessedFromMyDashboard" class="msg-text">
                        You can also manage this later in <strong class="clickable" @click="$router.push('/mysettings')" title="Click to navigate to My Setings page">My Settings &gt; Trusted Devices</strong>.
                    </p>
                </v-flex>
            </v-layout>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
            <v-spacer v-if="!isMobile"></v-spacer>
            <v-btn class="primary no-uppercase" block dark @click="enableDeviceLogin" :loading="loading">
                <v-icon v-if="isMobile">fingerprint</v-icon><span class="mr-5" v-if="isMobile"></span>
                Enable on this Device
            </v-btn>
            <v-btn v-if="accessedFromMyDashboard" class="no-uppercase" @click="handleNotNow" :disabled="loading">Not Now</v-btn>
            <v-btn v-if="!accessedFromMyDashboard" class="no-uppercase" @click="closeDialog" :disabled="loading">Cancel</v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>

import { setDeviceLoginNotNow } from '@/helpers/user-prefs.js'
import { base64urlToBuffer, bufferToBase64url } from '@/helpers/webauthn-utils.js'
import { getUAData } from '@/helpers/user-agent.js'
import webauthnApi from '@/api/webauthn'

export default {
    data () {
        return {
            error: false,
            errorTitle: null,
            errorMessage: null,
            loading: false,
        }
    },
    props: ['accessedFromMyDashboard'],
    methods: {
        openDeviceLoginUserGuide() {
            window.open('https://help.sweldonow.ph/guide/account-access.html')
        },
        async enableDeviceLogin() {
            try {
                // Reset error state on new attempt
                this.error = false
                this.errorMessage = null
                this.loading = true

                // 1. Get Registration Options from API
                const startRegistrationResponse = await webauthnApi.startRegistration()
                const options = startRegistrationResponse.data

                // 2. Prepare options for the browser (Convert strings to Buffers)
                const publicKey = {
                    ...options,
                    challenge: base64urlToBuffer(options.challenge),
                    user: {
                        ...options.user,
                        id: base64urlToBuffer(options.user.id)
                    },
                    // Convert any excluded credentials if they exist
                    excludeCredentials: (options.excludeCredentials || []).map(c => ({
                        ...c,
                        id: base64urlToBuffer(c.id)
                    }))
                };

                // 3. Trigger Browser Biometric Prompt
                const credential = await navigator.credentials.create({ publicKey })

                // 4. Get current device fingerprint for mapping                
                const ua = await getUAData()

                // 5. Format Response for Finish API                
                const response = {
                    id: credential.id,
                    rawId: bufferToBase64url(credential.rawId),
                    type: credential.type,
                    deviceFingerprint: ua.fingerprint,
                    transports: credential.response.getTransports ? credential.response.getTransports() : [],
                    response: {
                        clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
                        attestationObject: bufferToBase64url(credential.response.attestationObject)
                    }
                };

                // 6. Send to Finish API                
                const finishRegistrationResponse = await webauthnApi.finishRegistration(response)
                const result = finishRegistrationResponse.data

                if (result.success) {                    
                    this.$emit('success', ua.deviceName)
                    this.closeDialog()
                } else {       
                    this.$emit('fail', ua.deviceName)      
                    this.closeDialog()    
                    throw new Error(result.errorMessage || "Verification failed");
                }

            } catch (error) {
                this.error = true;

                // 1. User cancelled (NOT really an error → softer tone)
                if (error.name === 'NotAllowedError') {
                    this.errorTitle = "Setup not completed";
                    this.errorMessage = "You cancelled the request or it took too long. You can try again anytime.";
                } 
                // 2. Server-side error
                else if (error.response && error.response.data) {
                    this.errorTitle = "Something went wrong";
                    this.errorMessage = error.response.data.errorMessage || 
                        "We couldn't complete the setup. Please try again.";
                } 
                // 3. Network / unknown
                else {
                    this.errorTitle = "Connection issue";
                    this.errorMessage = "Please check your internet connection and try again.";
                }
            } finally {
                this.loading = false
            }
        },
        handleNotNow() {
            setDeviceLoginNotNow();
            this.closeDialog();
        },
        closeDialog() {
            this.error = false
            this.$emit('update:dialogEnableDeviceLogin', false)
        },
    },
    created() {},
    computed: {},
}
</script>
<style scoped>
.msg-text {
    margin:10px 20px 10px 20px;
    text-align: left;
}
.clickable:hover{
    text-decoration: underline;
}
</style>